import type { MapBounds } from '@/services/consumerService'

// The gateway groups markers with ST_SnapToGrid(point, cell), which rounds each
// point to the nearest grid node. So cell k spans ((k - 0.5) * cell, (k + 0.5) * cell)
// and tile edges must sit on half-cells, or every cell on an edge is split in two.

// A cell is an eighth of a 256 px map tile, about 32 px wide at any zoom.
// 64 cells is about 2048 px, so a viewport up to that size needs at most 2 x 2 tiles.
// Each tile is one query that reads all of the site's premises, so fewer, bigger
// tiles cost the server less than more, smaller ones.
export const TILE_CELLS = 64

export type MarkerTile = { x: number; y: number }

// The grid the server cuts. zoom is the level sent as `zoom`; it can be finer
// than the map's zoom. cells is how many cells wide a tile is.
export type Grid = { zoom: number; cells: number }

export const cellSize = (zoom: number) => 360 / 2 ** (zoom + 3)

// Each step halves the cell, so a set of few meters shows as single pins at a
// lower zoom; separateClusters still merges the icons that would overlap. A
// tile gets twice as many cells per step, so it covers the same area as at
// the map's zoom and a view still needs about 2 x 2 tiles. The steps depend
// only on how many meters the site and filter set put on the map, so the grid
// stays the same while the user pans, and tiles cached for it stay usable.
const finerSteps = (meters: number) =>
  meters < 2_000 ? 3 : meters < 10_000 ? 2 : meters < 40_000 ? 1 : 0

export const gridFor = (mapZoom: number, meters: number): Grid => {
  const steps = finerSteps(meters)
  return { zoom: mapZoom + steps, cells: TILE_CELLS * 2 ** steps }
}

const firstTile = (edge: number, cell: number, cells: number) =>
  Math.floor((edge / cell + 0.5) / cells)

const lastTile = (edge: number, cell: number, cells: number) =>
  Math.ceil((edge / cell + 0.5) / cells) - 1

export const tilesCovering = (
  box: MapBounds,
  zoom: number,
  cells = TILE_CELLS
): MarkerTile[] => {
  const cell = cellSize(zoom)
  // Google reports xmin > xmax when the view crosses the 180th meridian.
  const [xmin, xmax] = box.xmin > box.xmax ? [-180, 180] : [box.xmin, box.xmax]
  const [x0, x1] = [firstTile(xmin, cell, cells), lastTile(xmax, cell, cells)]
  const [y0, y1] = [
    firstTile(box.ymin, cell, cells),
    lastTile(box.ymax, cell, cells),
  ]
  const tiles: MarkerTile[] = []
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) tiles.push({ x, y })
  }
  return tiles
}

export const tileBounds = (
  tile: MarkerTile,
  zoom: number,
  cells = TILE_CELLS
): MapBounds => {
  const cell = cellSize(zoom)
  return {
    xmin: (tile.x * cells - 0.5) * cell,
    ymin: (tile.y * cells - 0.5) * cell,
    xmax: ((tile.x + 1) * cells - 0.5) * cell,
    ymax: ((tile.y + 1) * cells - 0.5) * cell,
  }
}

// The box of whole cells that the tiles covering `box` span together.
export const coverBounds = (
  box: MapBounds,
  zoom: number,
  cells = TILE_CELLS
): MapBounds => {
  const tiles = tilesCovering(box, zoom, cells)
  const first = tileBounds(tiles[0], zoom, cells)
  const last = tileBounds(tiles[tiles.length - 1], zoom, cells)
  return {
    xmin: first.xmin,
    ymin: first.ymin,
    xmax: last.xmax,
    ymax: last.ymax,
  }
}
